"use client"

import type { ReactNode } from "react"
import React, { useId } from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import * as LabelPrimitive from "@radix-ui/react-label"
import clsx from "clsx"
import { AnimatePresence, motion } from "motion/react"

import { cn } from "@productlane/lib/src/cn"

export interface CheckboxProps extends CheckboxPrimitive.CheckboxProps {
	name?: string
	label?: ReactNode
	errorMessage?: ReactNode
	description?: ReactNode
	direction?: "box-first" | "text-first"
	labelClassName?: string
	indicatorClassName?: string
	size?: "sm" | "md"
}

export const Checkbox = React.memo(
	React.forwardRef<HTMLDivElement, CheckboxProps>(
		(
			{
				className,
				label,
				direction = "box-first",
				errorMessage,
				description,
				labelClassName,
				indicatorClassName,
				name,
				size = "md",
				...props
			},
			ref,
		) => {
			const id = useId()

			return (
				<div ref={ref} className={clsx("", className)}>
					<div
						className={clsx(
							"flex items-center gap-3",
							direction === "text-first" ? "flex-row-reverse" : "flex-row",
							props.disabled && "pointer-events-none",
						)}
					>
						{/* Initial false so that when a checkbox renders that is already checked we don't play the animation */}
						<AnimatePresence initial={false}>
							<CheckboxPrimitive.Root
								{...props}
								id={id}
								className={cn(
									"flex flex-none items-center justify-center rounded border border-gray-300 bg-white text-white hover:border-brand-500 hover:bg-brand-50 focus:outline-none focus-visible:ring-4 data-[state=checked]:border-green-500 data-[state=indeterminate]:border-green-500 data-[state=checked]:bg-green-500 data-[state=indeterminate]:bg-green-500 data-[state=checked]:hover:bg-green-500 data-[state=indeterminate]:hover:bg-green-500 dark:border-gray-600 dark:bg-gray-900 dark:hover:bg-brand-800",
									size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5",
									props.disabled && "bg-gray-200 dark:bg-gray-600",
									indicatorClassName,
								)}
							>
								<CheckboxPrimitive.CheckboxIndicator
									className="hidden text-sm data-[state=indeterminate]:block"
									asChild
								>
									{/* Minus icon */}
									<svg
										width="1em"
										height="1em"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<motion.path
											d="M5 12H19"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											initial={{ pathLength: 0 }}
											animate={{ pathLength: 1 }}
											transition={{ duration: 0.1 }}
										/>
									</svg>
								</CheckboxPrimitive.CheckboxIndicator>
								<CheckboxPrimitive.CheckboxIndicator
									className="hidden text-sm data-[state=checked]:block"
									asChild
								>
									<svg
										width="1em"
										height="1em"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<motion.path
											d="M4,12L9,17L20,6"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											initial={{ pathLength: 0 }}
											animate={{ pathLength: 1 }}
											transition={{ duration: 0.1 }}
										/>
									</svg>
								</CheckboxPrimitive.CheckboxIndicator>
							</CheckboxPrimitive.Root>
						</AnimatePresence>

						{label && (
							<LabelPrimitive.Root
								htmlFor={id}
								className={cn(
									"block min-w-16 text-left text-sm font-medium",
									labelClassName,
								)}
							>
								{label}
								{description && (
									<div className="mt-1 text-left font-normal text-secondary">
										{description}
									</div>
								)}
							</LabelPrimitive.Root>
						)}
					</div>

					{errorMessage && (
						<div
							className={clsx(
								"mt-1.5 text-left text-sm",
								errorMessage ? "text-red-500" : "text-gray-600",
							)}
							id={`${name}-error`}
						>
							{errorMessage}
						</div>
					)}
				</div>
			)
		},
	),
)
